import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetProductFullView } from 'hooks';
import { useSafeProductIdParam } from 'hooks/useProductFullView';
import { flattenTooltipForFields } from 'pages/help-content-management/utils';
import { useCallback, useMemo } from 'react';
import { getHelpContentTooltipMember } from 'services/help-content-management';

export const getTooltipForMember = (memberId) => {
  return ['help-content-management', 'tooltips-member', parseInt(memberId)];
};

export const useGetTooltipForMember = () => {
  const { id } = useSafeProductIdParam();

  const { productFull } = useGetProductFullView({
    productId: id,
  });

  const ownerId = productFull?.ownerId;

  const queryClient = useQueryClient();

  const queries = useQuery({
    queryKey: getTooltipForMember(ownerId),
    queryFn: async () => {
      const response = await getHelpContentTooltipMember({ memberId: ownerId });

      if (response?.isSuccess) {
        return response.data;
      }
    },
    enabled: !!ownerId,
    retry: 0,
  });

  const invalidateTooltipQueries = useCallback(
    (memberIdProp) => {
      queryClient.invalidateQueries({
        queryKey: getTooltipForMember(memberIdProp),
      });
    },
    [queryClient]
  );

  return {
    ...queries,
    flattenedTooltipFields: useMemo(() => {
      return flattenTooltipForFields(queries.data?.qaSpecTooltips ?? []);
    }, [queries?.data]),
    invalidateTooltipQueries,
  };
};

export const useGetTooltipContentByFieldPath = () => {
  const { flattenedTooltipFields } = useGetTooltipForMember();

  const getTooltipContentByPath = useCallback(
    (fieldPath) => {
      return flattenedTooltipFields[fieldPath]?.tooltipContent ?? 'N/A';
    },
    [flattenedTooltipFields]
  );

  const isExistTooltipContentByPath = useCallback(
    (fieldPath) => {
      return !!flattenedTooltipFields[fieldPath];
    },
    [flattenedTooltipFields]
  );

  return {
    isExistTooltipContentByPath,
    getTooltipContentByPath,
  };
};
